/**
 * Os papéis do modelo aprovado, e as perguntas que as telas fazem sobre
 * eles.
 *
 * **Espelho de `core/vocabulary.py`** (`ROLES`, `role_at_least`), que é o
 * dono. Duplicação entre linguagens, inevitável e do mesmo tipo da regra
 * de janela em `date-range.ts`: se um dia divergirem, o servidor vence —
 * ele recusa com 403 e a tela mostra um botão que não funciona, que é o
 * sintoma a procurar.
 *
 * **O que havia aqui até 2026-08-29 era um dialeto ÓRFÃO**:
 * `["administrator", "admin", "master_admin"]`, herdado do backend
 * descartado, gateando CINCO telas vivas (CompanyHub, SettingsPage,
 * AdminLayout, AppShellProvider, Sidebar) com papéis que nunca iam
 * chegar — o backend real nunca emitiu nenhum dos três. Não era código
 * morto: era código vivo decidindo com um vocabulário morto.
 *
 * **Os nomes mudaram em 2026-10-22** (proposta de IAM, chave = nome): o
 * topo, que era `administrator`, virou `master`; o `coordinator` virou
 * `administrator`; o `analyst` virou `specialist`; e entrou o `viewer`,
 * que só vê. Em texto anterior a essa data, "Administrador" é o Máster
 * de hoje e "Coordenador" é o Administrador.
 */

/** Do maior alcance pro menor — a mesma ordem de `ROLES` no `core`, e é
 *  ela que `roleAtLeast` percorre. */
export const ROLES = ["master", "administrator", "specialist", "technician", "viewer"] as const;

export type Role = (typeof ROLES)[number];

/**
 * O nome do cargo na língua da tela. Mora aqui, e não em cada tela de
 * acesso, porque Usuários e Núcleos listam a mesma pessoa e divergiam:
 * uma dizia "Analista", a outra o código cru `analyst` (2026-10-09,
 * teste de render de Núcleos). Fecha o conjunto: cargo novo em `ROLES`
 * sem rótulo aqui é erro de compilação.
 */
export const ROLE_LABEL: Record<Role, string> = {
  master: "Máster",
  administrator: "Administrador",
  specialist: "Especialista",
  technician: "Técnico",
  viewer: "Visualizador"
};

/**
 * O papel alcança pelo menos `minimum`?
 *
 * Espelha `core/vocabulary.py::role_at_least`, inclusive no fail-closed:
 * papel que não está na escada não alcança nada. É o que impede um claim
 * desconhecido (ou um resquício do dialeto antigo) de abrir tela.
 */
export function roleAtLeast(role: string, minimum: Role): boolean {
  const rank = ROLES.indexOf(role as Role);
  return rank !== -1 && rank <= ROLES.indexOf(minimum);
}

/** Alguma das credenciais alcança `minimum`? */
export function hasRoleAtLeast(
  profile: { roles: string[] },
  minimum: Role
): boolean {
  return profile.roles.some(role => roleAtLeast(role, minimum));
}

/**
 * Máster — o topo. Acesso a tudo, todas as empresas, config geral do
 * CENA, e a gestão de todos os usuários.
 */
export function isMaster(profile: { roles: string[] }): boolean {
  return hasRoleAtLeast(profile, "master");
}

/**
 * Administrador ou acima — todas as empresas do seu núcleo, e a gestão
 * dos usuários abaixo dele no núcleo.
 *
 * Este predicado responde apenas "tem o papel". Núcleos, vínculos e
 * empresas visíveis são resolvidos pelo backend nas tabelas locais; a UI
 * nunca decide autorização pelo conteúdo que esconde ou exibe.
 */
export function isAdministrator(profile: { roles: string[] }): boolean {
  return hasRoleAtLeast(profile, "administrator");
}

/**
 * Técnico ou acima — quem OPERA: edita classificação, revisa, exporta,
 * sobe planilha, gera relatório. O Visualizador (2026-10-22) não passa:
 * ele só vê, e a tela esconde as ações em vez de mostrar um botão que o
 * servidor recusa.
 */
export function canOperate(profile: { roles: string[] }): boolean {
  return hasRoleAtLeast(profile, "technician");
}
